/**
 * AdminNummernkreiseBestandSection — Bestandsanalyse Rechnungsnummern (Issue #485)
 *
 * Zeigt den View rechnungsnummern_abweichungen als gefilterte Liste.
 * Filter: Betrieb, Jahr. CSV-Export aller sichtbaren Zeilen.
 */

import { useState, useMemo, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, Download, RefreshCw, XCircle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useBetriebe } from '@/hooks/useSupabase';
import { localIsoDate } from '@/lib/date-utils';

interface AbweichungRow {
  id: string;
  rechnungsnummer: string;
  betrieb_id: string;
  business_unit: string;
  rechnungsjahr: number;
  erwartetes_praefix: string | null;
  passt: boolean | null;
}

const CURRENT_YEAR = new Date().getFullYear();
const JAHRE = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

function useAbweichungen(betriebId: string, jahr: number) {
  const [data, setData] = useState<AbweichungRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('rechnungsnummern_abweichungen')
        .select('*')
        .eq('rechnungsjahr', jahr)
        .order('rechnungsnummer', { ascending: true });

      if (betriebId) {
        query = query.eq('betrieb_id', betriebId);
      }

      const { data: rows, error: err } = await query;
      if (err) throw err;
      setData((rows ?? []) as AbweichungRow[]);
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [betriebId, jahr]);

  return { data, loading, error, refetch: load };
}

function passtBadge(passt: boolean | null) {
  if (passt === true) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
        <CheckCircle2 className="w-3 h-3" />
        Passt
      </span>
    );
  }
  if (passt === false) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
        <XCircle className="w-3 h-3" />
        Abweichung
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
      <HelpCircle className="w-3 h-3" />
      Kein Kreis
    </span>
  );
}

function exportCsv(rows: AbweichungRow[]) {
  const header = ['id', 'rechnungsnummer', 'betrieb_id', 'business_unit', 'rechnungsjahr', 'erwartetes_praefix', 'passt'];
  const lines = rows.map(r => [
    r.id,
    r.rechnungsnummer,
    r.betrieb_id,
    r.business_unit,
    r.rechnungsjahr,
    r.erwartetes_praefix ?? '',
    r.passt === null ? '' : r.passt ? 'true' : 'false',
  ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

  const csv = [header.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rechnungsnummern-bestand-${localIsoDate(new Date())}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminNummernkreiseBestandSection() {
  const [betriebId, setBetriebId] = useState('');
  const [jahr, setJahr] = useState(CURRENT_YEAR);
  const { data: betriebe } = useBetriebe();
  const { data, loading, error, refetch } = useAbweichungen(betriebId, jahr);

  const [hasLoaded, setHasLoaded] = useState(false);

  const handleLoad = useCallback(async () => {
    setHasLoaded(true);
    await refetch();
  }, [refetch]);

  const stats = useMemo(() => ({
    gesamt: data.length,
    passt: data.filter(r => r.passt === true).length,
    abweichung: data.filter(r => r.passt === false).length,
    keinKreis: data.filter(r => r.passt === null).length,
  }), [data]);

  const handleCsvExport = () => {
    if (data.length === 0) {
      toast.info('Keine Daten für den Export. Zuerst Bericht laden.');
      return;
    }
    exportCsv(data);
    toast.success(`${data.length} Zeilen exportiert`);
  };

  return (
    <div data-testid="nummernkreise-bestand-section">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Bestandsanalyse Rechnungsnummern</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Prüft für jede Rechnung ob die Nummer dem konfigurierten Schema entspricht.
              Altdaten (Rentsoft) erscheinen als „Abweichung".
            </p>
          </div>
          <button
            onClick={handleCsvExport}
            data-testid="bestand-csv-export"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 block">Betrieb</label>
            <select
              value={betriebId}
              onChange={e => setBetriebId(e.target.value)}
              data-testid="bestand-filter-betrieb"
              className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
            >
              <option value="">Alle Betriebe</option>
              {(betriebe ?? []).map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 block">Jahr</label>
            <select
              value={jahr}
              onChange={e => setJahr(Number(e.target.value))}
              data-testid="bestand-filter-jahr"
              className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
            >
              {JAHRE.map(j => (
                <option key={j} value={j}>{j}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleLoad}
            disabled={loading}
            data-testid="bestand-load-report"
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#9B1C1C] text-white text-xs font-medium hover:bg-[#7A1616] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Lade…' : 'Bericht laden'}
          </button>
        </div>
      </div>

      {/* Stats (nur nach dem Laden) */}
      {hasLoaded && !loading && !error && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Gesamt', value: stats.gesamt, color: 'text-gray-700', bg: 'bg-gray-50 border-gray-200' },
            { label: 'Passt', value: stats.passt, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
            { label: 'Abweichung', value: stats.abweichung, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
            { label: 'Kein Kreis', value: stats.keinKreis, color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className={`rounded-xl border ${bg} px-4 py-3`}>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
              <p className={`text-xl font-bold ${color} mt-0.5`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-4 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Initial state */}
      {!hasLoaded && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-col items-center justify-center py-14 text-gray-400">
            <RefreshCw className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-sm font-medium">Noch kein Bericht geladen</p>
            <p className="text-xs mt-1">Filter auswählen und „Bericht laden" klicken.</p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-center py-12 text-sm text-gray-400">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Lade Bestandsdaten…
          </div>
        </div>
      )}

      {/* Empty state after load */}
      {hasLoaded && !loading && !error && data.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <CheckCircle2 className="w-8 h-8 mb-2 opacity-40 text-green-500" />
            <p className="text-sm font-medium">Keine Rechnungen für diesen Filter</p>
            <p className="text-xs mt-1">Betrieb oder Jahr anpassen.</p>
          </div>
        </div>
      )}

      {/* Table */}
      {hasLoaded && !loading && !error && data.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Rechnungsnummer</th>
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Bereich</th>
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Jahr</th>
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Erw. Präfix</th>
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr
                    key={row.id}
                    className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${row.passt === false ? 'bg-red-50/30' : ''}`}
                  >
                    <td className="py-2.5 px-4 font-mono text-xs font-semibold text-gray-800">{row.rechnungsnummer}</td>
                    <td className="py-2.5 px-4 text-xs text-gray-600">{row.business_unit}</td>
                    <td className="py-2.5 px-4 text-xs text-gray-600">{row.rechnungsjahr}</td>
                    <td className="py-2.5 px-4 font-mono text-xs text-gray-500">
                      {row.erwartetes_praefix ?? <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-2.5 px-4">{passtBadge(row.passt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-4 py-2.5 flex justify-between items-center">
            <p className="text-[11px] text-gray-400">{data.length} Einträge</p>
            <button
              onClick={handleCsvExport}
              className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 transition-colors"
            >
              <Download className="w-3 h-3" />
              CSV exportieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
